/**
 * Диалог подтверждения выгрузки — последний человеческий барьер перед
 * необратимым действием на площадке. Общий для каталога товаров и вкладок
 * «Черновик PIM» каталогов ВБ и ОЗ: текст и счётчики зависят от области
 * действия, а не от места вызова.
 */

import { useEffect, useState } from 'react'
import { AlertTriangle, CloudUpload } from 'lucide-react'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'
import { marketplaceLabel, plural, publishScopeLabel, publishScopeWarning } from './publishScope'
import type { PublishPlan } from './types'

const MAX_VISIBLE_ARTICLES = 12

interface PublishConfirmDialogProps {
  isOpen: boolean
  plan: PublishPlan | null
  pending: boolean
  onClose: () => void
  onConfirm: () => void
}

export function PublishConfirmDialog({
  isOpen,
  plan,
  pending,
  onClose,
  onConfirm,
}: PublishConfirmDialogProps) {
  const [confirmed, setConfirmed] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  // Каждое открытие — новое решение человека: галочка не наследуется.
  useEffect(() => {
    setConfirmed(false)
    setSubmitted(false)
  }, [isOpen, plan])

  if (!plan) return null

  const nothingToPublish = plan.cardCount === 0
  const confirmLabel = `Выгрузить ${plan.productCount} ${plural(plan.productCount, [
    'товар',
    'товара',
    'товаров',
  ])}`

  const close = () => {
    if (pending) return
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title="Подтвердите выгрузку на маркетплейсы"
      subtitle={`Область выгрузки: ${publishScopeLabel(plan.scope)}`}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={pending}>
            Отмена
          </Button>
          <Button
            variant="primary"
            icon={<CloudUpload className="h-4 w-4" />}
            disabled={!confirmed || nothingToPublish || pending || submitted}
            loading={pending}
            onClick={() => {
              if (!confirmed || nothingToPublish || submitted) return
              setSubmitted(true)
              onConfirm()
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <p>{publishScopeWarning(plan.scope)}</p>
        </div>

        <dl
          className="grid grid-cols-3 gap-2 rounded-lg border border-gray-200 p-3 text-center dark:border-zinc-700"
          aria-label="Объём выгрузки"
        >
          <div>
            <dt className="text-2xs uppercase tracking-wider text-gray-500 dark:text-zinc-400">
              Товаров
            </dt>
            <dd className="text-lg font-semibold tabular-nums text-gray-900 dark:text-zinc-100">
              {plan.productCount}
            </dd>
          </div>
          <div>
            <dt className="text-2xs uppercase tracking-wider text-gray-500 dark:text-zinc-400">
              Карточек
            </dt>
            <dd className="text-lg font-semibold tabular-nums text-gray-900 dark:text-zinc-100">
              {plan.cardCount}
            </dd>
          </div>
          <div>
            <dt className="text-2xs uppercase tracking-wider text-gray-500 dark:text-zinc-400">
              Кабинетов
            </dt>
            <dd className="text-lg font-semibold tabular-nums text-gray-900 dark:text-zinc-100">
              {plan.cabinets.length}
            </dd>
          </div>
        </dl>

        {nothingToPublish ? (
          <p className="rounded-lg border border-gray-200 p-3 text-sm text-amber-700 dark:border-zinc-700 dark:text-amber-300">
            Выгружать нечего: среди выбранного нет карточек в статусе «Черновик» для этой области.
          </p>
        ) : (
          <div className="space-y-2" aria-label="Состав выгрузки">
            {plan.cabinets.map((cabinet) => (
              <section
                key={cabinet.key}
                className="rounded-lg border border-gray-200 p-3 dark:border-zinc-700"
              >
                <h4 className="flex items-baseline justify-between gap-2 text-sm font-semibold text-gray-900 dark:text-zinc-100">
                  <span>
                    {marketplaceLabel(cabinet.marketplace)} · кабинет {cabinet.cabinet}
                  </span>
                  <span className="text-2xs font-normal tabular-nums text-gray-500 dark:text-zinc-400">
                    {cabinet.targets.length}{' '}
                    {plural(cabinet.targets.length, ['карточка', 'карточки', 'карточек'])}
                  </span>
                </h4>
                <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[0.8125rem] text-gray-700 dark:text-zinc-300">
                  {cabinet.targets.slice(0, MAX_VISIBLE_ARTICLES).map((target) => (
                    <li key={`${target.article}|${cabinet.key}`} className="font-mono">
                      {target.article}
                    </li>
                  ))}
                  {cabinet.targets.length > MAX_VISIBLE_ARTICLES && (
                    <li className="text-gray-500 dark:text-zinc-400">
                      и ещё {cabinet.targets.length - MAX_VISIBLE_ARTICLES}
                    </li>
                  )}
                </ul>
              </section>
            ))}
          </div>
        )}

        <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-gray-200 p-3 text-sm text-gray-800 dark:border-zinc-700 dark:text-zinc-200">
          <input
            type="checkbox"
            aria-label="Подтвердить выгрузку на маркетплейсы"
            checked={confirmed}
            disabled={nothingToPublish || pending || submitted}
            onChange={(event) => setConfirmed(event.target.checked)}
            className="mt-0.5 h-4 w-4 accent-indigo-600"
          />
          <span>Подтверждаю выгрузку карточек на маркетплейсы.</span>
        </label>
      </div>
    </Modal>
  )
}

export default PublishConfirmDialog
